import { useState } from "react";
import { useListLeads, useUpdateLeadStatus, useDeleteLead } from "@workspace/api-client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Trash2, Eye, Mail, Briefcase, Globe, Clock, MessageSquare, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function LeadDetailsDialog({ lead, isOpen, onClose }: { lead: any, isOpen: boolean, onClose: () => void }) {
  if (!lead) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] bg-card border-border/50">
        <DialogHeader className="border-b border-border/50 pb-4">
          <DialogTitle className="font-display text-2xl font-bold flex items-center gap-3">
            <Info className="h-6 w-6 text-primary" />
            Lead Details
          </DialogTitle>
          <p className="text-sm text-muted-foreground">Received on {format(new Date(lead.createdAt), 'PPPP p')}</p>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-6">
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">Contact Information</Label>
              <div className="flex items-center gap-3 mt-1">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                  {lead.name.charAt(0)}
                </div>
                <div>
                  <p className="font-bold">{lead.name}</p>
                  <p className="text-sm text-muted-foreground">{lead.email}</p>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">Source & Service</Label>
              <div className="space-y-2 mt-2">
                <div className="flex items-center gap-2 text-sm">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Source:</span> {lead.source || 'Website'}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Briefcase className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Service:</span> {lead.service || '-'}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">Budget & Timeline</Label>
              <div className="space-y-2 mt-2">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Budget:</span> {lead.budget || 'Not Specified'}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Timeline:</span> {lead.timeline || 'Not Specified'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-border/50 pt-6">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Message / Inquiry</Label>
          <div className="bg-muted/30 rounded-lg p-4 text-sm leading-relaxed whitespace-pre-wrap border border-border/50 max-h-[250px] overflow-auto">
            {lead.message || "No message provided."}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => window.open(`mailto:${lead.email}?subject=Re: AccelerateByte Inquiry`)}>
            <Mail className="h-4 w-4 mr-2" /> Reply via Email
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Minimal components for the dialog
const Label = ({ children, className }: any) => <span className={`text-sm font-medium ${className}`}>{children}</span>;
const CalendarIcon = ({ className }: any) => <Clock className={className} />;

export default function Leads() {
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [selectedLead, setSelectedLead] = useState<any>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  
  const { data: leadsData, isLoading, refetch } = useListLeads(
    statusFilter && statusFilter !== 'ALL' ? { status: statusFilter } : {}
  );
  
  const updateStatus = useUpdateLeadStatus();
  const deleteLead = useDeleteLead();

  const leads = leadsData?.leads || [];

  const handleStatusChange = (id: string, newStatus: any) => {
    updateStatus.mutate({ id, data: { status: newStatus } }, {
      onSuccess: () => refetch()
    });
  };

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (confirm("Are you sure you want to delete this lead?")) {
      deleteLead.mutate({ id }, {
        onSuccess: () => refetch()
      });
    }
  };

  const openDetails = (lead: any) => {
    setSelectedLead(lead);
    setIsDetailsOpen(true);
    if (lead.status === 'NEW') {
      handleStatusChange(lead.id, 'READ');
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold mb-2">Leads CRM</h1>
          <p className="text-muted-foreground">Manage incoming inquiries and project requests.</p>
        </div>
        
        <div className="flex items-center gap-4">
          <Select value={statusFilter || "ALL"} onValueChange={(v) => setStatusFilter(v === "ALL" ? null : v)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Statuses</SelectItem>
              <SelectItem value="NEW">New</SelectItem>
              <SelectItem value="READ">Read</SelectItem>
              <SelectItem value="REPLIED">Replied</SelectItem>
              <SelectItem value="CONVERTED">Converted</SelectItem>
              <SelectItem value="LOST">Lost</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-card border border-border/50 rounded-xl overflow-hidden shadow-sm">
        <Table>
          <TableHeader className="bg-muted/50">
            <TableRow>
              <TableHead className="w-[120px]">Date</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Service / Subject</TableHead>
              <TableHead>Message Snippet</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-20">
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">Loading leads...</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : leads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                  <div className="flex flex-col items-center gap-2">
                    <MessageSquare className="h-8 w-8 opacity-20" />
                    <p>No leads found matching your criteria.</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              leads?.map((lead: any) => (
                <TableRow 
                  key={lead.id} 
                  className="cursor-pointer hover:bg-muted/30 transition-colors group border-b border-border/40"
                  onClick={() => openDetails(lead)}
                >
                  <TableCell className="whitespace-nowrap py-4 text-[10px] text-muted-foreground font-medium">
                    {format(new Date(lead.createdAt), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="font-semibold text-sm">{lead.name}</div>
                    <div className="text-[10px] text-muted-foreground">{lead.email}</div>
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="text-sm">{lead.service || '-'}</div>
                    <div className="text-[10px] text-muted-foreground uppercase">{lead.subject || '-'}</div>
                  </TableCell>
                  <TableCell className="max-w-[400px] py-4">
                    <p className="text-xs text-muted-foreground line-clamp-1 italic">
                      {lead.message || "No message."}
                    </p>
                  </TableCell>
                  <TableCell className="py-4" onClick={(e) => e.stopPropagation()}>
                    <Select value={lead.status} onValueChange={(v) => handleStatusChange(lead.id, v)}>
                      <SelectTrigger className={`h-7 w-[110px] text-[10px] font-bold border-0 ${
                        lead.status === 'NEW' ? 'bg-blue-500/15 text-blue-500' :
                        lead.status === 'READ' ? 'bg-amber-500/15 text-amber-500' :
                        lead.status === 'CONVERTED' ? 'bg-emerald-500/15 text-emerald-500' :
                        lead.status === 'LOST' ? 'bg-muted text-muted-foreground' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NEW">NEW</SelectItem>
                        <SelectItem value="READ">READ</SelectItem>
                        <SelectItem value="REPLIED">REPLIED</SelectItem>
                        <SelectItem value="CONVERTED">CONVERTED</SelectItem>
                        <SelectItem value="LOST">LOST</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right py-4 pr-4">
                    <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-primary">
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={(e) => handleDelete(e, lead.id)} 
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <LeadDetailsDialog 
        lead={selectedLead} 
        isOpen={isDetailsOpen} 
        onClose={() => setIsDetailsOpen(false)} 
      />
    </div>
  );
}

function Loader2({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`animate-spin ${className}`}
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}
